import localization from '../../../shared/strings';
import * as utils from '../../../shared/utils';
import Toast from '../../../components/Toast';
import TYPES from '../../action-types';
import Logger from '../../../services/Logger';
import asyncCheckMultipleChanges from './asyncCheckMultipleChanges';
import { reloadCurrentPage } from '../../../helpers/history';
import { showWarningDetachingSeveralKeywords } from '../../helpers/assets';
import { keywordsRemovedFromSelection } from '../../helpers/keywordsRemovedByCascade';
import { updateCount as updateCountAction } from '../../keywords/actions';
import sdk from '../../../sdk';

/**
 * Remove keyword from assets
 * @param {string} keywordName
 * @param {string[]?} ids - assets IDs
 */
const detachKeywords = (keywordIds, assetIds) => async (dispatch, getState) => {
  const { items, selectedAssetsAggregatedData } = getState().assets;
  const selectedItems = assetIds || getState().assets.selectedItems;
  const keywordsToRemove = keywordsRemovedFromSelection(keywordIds, {
    selectedIds: selectedItems,
    items,
    aggregatedKeywords: selectedAssetsAggregatedData?.keywords,
  });
  /**
   * One child stays at 1: cascade removes descendants, not that child's parents.
   * Requested ids still count when only some of them are on in-memory assets.
   * Count can be higher than the server result when selected assets are not loaded;
   * see keywordsRemovedFromSelection. It never changes what is sent to the API.
   */
  const keywordCount = new Set([
    ...keywordIds.map((id) => String(id)),
    ...keywordsToRemove,
  ]).size;
  if (keywordCount > 1) {
    const res = await showWarningDetachingSeveralKeywords(keywordCount, selectedItems.length);
    if (!res) {
      /** User has cancelled operation */
      return false;
    }
  }

  try {
    await asyncCheckMultipleChanges(selectedItems.length);
  } catch (error) {
    /** User has cancelled operation */
    return false;
  }

  try {
    dispatch({ type: TYPES.ASSETS.DETACH_KEYWORD.START });

    /** wait for response */
    const { data } = await sdk.keywords.detachMany(selectedItems, keywordIds);

    if (data.hasNotPermittedAssets) {
      Toast(localization.NO_COLLECTION_PERMISSION_TO_ACCESS('detach keywords'), { autoClose: false });
    }

    const detachedKeywordIds = Array.isArray(data.detachedKeywordIds)
      ? data.detachedKeywordIds.map((id) => String(id))
      : keywordIds;
    const detachments = Array.isArray(data.detachments)
      ? data.detachments.map((detachment) => ({
        assetId: String(detachment.assetId),
        keywordIds: detachment.keywordIds.map((id) => String(id)),
      }))
      : null;

    dispatch({
      type: TYPES.ASSETS.DETACH_KEYWORD.COMPLETE,
      payload: {
        ids: assetIds,
        keywordsIds: detachedKeywordIds,
        detachments,
        userId: getState().user._id,
      },
    });
    if (detachments) {
      const removedCounts = detachments.reduce((counts, { keywordIds: removedIds }) => {
        removedIds.forEach((id) => counts.set(id, (counts.get(id) || 0) + 1));
        return counts;
      }, new Map());
      removedCounts.forEach((count, id) => {
        dispatch(updateCountAction({ _id: id, value: -count }));
      });
    }
    return true;
  } catch (error) {
    Logger.error(new Error('Can not dettach keywords from asset'), { error }, [
      'DetachKeywordsFailed',
      { errorMessage: (error && error.message) || 'NoMessage' },
    ]);
    const errorStatus = utils.getStatusFromResponceError(error);
    const errorMessage = errorStatus === 403
      ? localization.NO_PERMISSION_TO_ACCESS
      : utils.getDataFromResponceError(error, 'msg');
    if (errorMessage) {
      Toast(errorMessage, { autoClose: false, type: 'error' });
    } else {
      Toast(localization.DETAILS.textCantUpdateKeywords, {
        autoClose: false,
        type: 'error',
        onOk: reloadCurrentPage,
        btnOkValue: localization.HISTORY.textBtnRefresh,
      });
    }
    dispatch({ type: TYPES.ASSETS.DETACH_KEYWORD.FAILED, error });
    return false;
  }
};

export default detachKeywords;
